import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

/**
 * 소요 시간 구성 — 타임라인 오른쪽 칸(2026-10-22 요청: 「1시간 중 30분은 sql1, 10분은 sql2」 를 막대로).
 *
 * 계산은 `utils/ashTimeline.timeComposition` 이 한다(세션 하나 = 벽시계 기준·비활성 포함, 세션 풀 = DB 시간 기준).
 * 위의 띠 하나가 100% 구성, 아래 줄마다 막대 하나 — SQL 막대 안은 CPU(초록)·대기(파랑)로 나눠 칠하고,
 * 커밋은 빗금으로 SQL 과 구분한다. 줄을 누르면 왼쪽 타임라인에서 그 SQL 만 진하게(onFocusSql).
 */

/** 띠의 SQL 색 — 순위 순서. 커밋·SQL 없음·기타·비활성은 따로 정해 둔다 */
const SQL_COLORS = ['#2563eb', '#7c3aed', '#0891b2', '#db2777', '#65a30d', '#ea580c', '#0d9488', '#9333ea'];
export const COMMIT_COLOR = '#f59e0b';
const CPU_COLOR = '#16a34a';
const WAIT_COLOR = '#3b82f6';

export function rowColor(row, sqlIndex) {
    if (row.kind === 'commit') return COMMIT_COLOR;
    if (row.kind === 'nosql') return '#94a3b8';
    if (row.kind === 'others') return '#cbd5e1';
    if (row.kind === 'idle') return 'transparent';
    return SQL_COLORS[sqlIndex % SQL_COLORS.length];
}

export default function TimeComposition({ comp, breakdown, commitStats, truncated, focusSql, onFocusSql, fmtDur }) {
    const { t } = useTranslation();
    if (!comp || comp.base <= 0) return null;
    const pct = (v) => `${Math.round(v * 1000) / 10}%`;
    const info = new Map((breakdown || []).map(b => [b.sqlId, b]));
    let sqlIdx = -1;
    const colored = comp.rows.map((r) => {
        if (r.kind === 'sql') sqlIdx += 1;
        return { ...r, color: rowColor(r, Math.max(0, sqlIdx)) };
    });
    const label = (r) => {
        if (r.kind === 'sql') return r.sqlId;
        if (r.kind === 'commit') return t('monitoring:sa.tc.commit');
        if (r.kind === 'nosql') return t('monitoring:sa.tc.noSql');
        if (r.kind === 'others') return t('monitoring:sa.tc.others', { n: r.count });
        return t(comp.pool ? 'monitoring:sa.tc.idle' : 'monitoring:sa.tc.idleSession');
    };
    const tip = (r) => {
        if (r.kind === 'sql') {
            const b = info.get(r.sqlId);
            return [
                r.sqlId,
                `CPU ${fmtDur(r.cpuMs)} · ${t('monitoring:sa.tl.wait')} ${fmtDur(r.waitMs)}`,
                b && b.topEvent ? `${t('monitoring:sa.tl.colTopEvent')}: ${b.topEvent}` : null,
                b && b.execCount ? `${t('monitoring:sa.tl.colExecs')}: ${b.execCount}` : null,
            ].filter(Boolean).join('\n');
        }
        if (r.kind === 'commit') return t('monitoring:sa.tc.commitHint');
        if (r.kind === 'nosql') return t('monitoring:sa.tc.noSqlHint');
        if (r.kind === 'idle') return t(comp.pool ? 'monitoring:sa.tc.idleHint' : 'monitoring:sa.tc.idleSessionHint');
        return '';
    };

    return (
        <div className="ash-tc">
            <div className="ash-tc-head">
                <b>{t('monitoring:sa.tc.title')}</b>
                <span>
                    {comp.pool
                        ? t('monitoring:sa.tc.basisPool', { db: fmtDur(comp.base), n: comp.sessions, avg: comp.avgActive < 0.01 ? '<0.01' : Math.round(comp.avgActive * 100) / 100 })
                        : t('monitoring:sa.tc.basisSession', { wall: fmtDur(comp.span) })}
                </span>
            </div>

            {/* 100% 띠 */}
            <div className="ash-tc-strip" role="img" aria-label={t('monitoring:sa.tc.title')}>
                {colored.map(r => (
                    <i key={r.key}
                        className={`ash-tc-strip-part kind-${r.kind}${focusSql && r.sqlId === focusSql ? ' on' : ''}`}
                        style={{ width: `${r.share * 100}%`, background: r.kind === 'commit' ? undefined : r.color }}
                        title={`${label(r)} · ${fmtDur(r.ms)} · ${pct(r.share)}`} />
                ))}
            </div>

            <div className="ash-tc-rows">
                {colored.map(r => (
                    <div key={r.key}
                        className={`ash-tc-row kind-${r.kind}${focusSql && r.sqlId === focusSql ? ' on' : ''}${r.kind === 'sql' ? ' clickable' : ''}`}
                        title={tip(r)}
                        onClick={r.kind === 'sql' && onFocusSql ? () => onFocusSql(r.sqlId) : undefined}>
                        <span className="ash-tc-name">
                            <i className={`ash-tc-dot kind-${r.kind}`} style={{ background: r.kind === 'commit' || r.kind === 'idle' ? undefined : r.color }} />
                            <span className={r.kind === 'sql' ? 'mono' : undefined}>{label(r)}</span>
                        </span>
                        <span className="ash-tc-track">
                            {r.kind === 'sql' || r.kind === 'others' || r.kind === 'nosql' ? (
                                <>
                                    <i className="ash-tc-fill" style={{ width: `${(r.cpuMs / comp.base) * 100}%`, background: CPU_COLOR }} />
                                    <i className="ash-tc-fill" style={{ width: `${(r.waitMs / comp.base) * 100}%`, background: WAIT_COLOR }} />
                                </>
                            ) : (
                                <i className={`ash-tc-fill kind-${r.kind}`} style={{ width: `${r.share * 100}%` }} />
                            )}
                        </span>
                        <span className="ash-tc-val">{fmtDur(r.ms)} · {pct(r.share)}</span>
                        {r.kind === 'commit' && commitStats && commitStats.commits != null && (
                            <span className="ash-tc-sub">
                                {t('monitoring:sa.tc.commitCount', { c: commitStats.commits.toLocaleString(), r: (commitStats.rollbacks ?? 0).toLocaleString() })}
                            </span>
                        )}
                    </div>
                ))}
                {/* 커밋 표본이 없어도 횟수는 있을 수 있다 — 짧은 커밋은 1초 표본에 안 잡힌다 */}
                {!colored.some(r => r.kind === 'commit') && commitStats && commitStats.commits != null && (
                    <div className="ash-tc-row kind-commit">
                        <span className="ash-tc-name"><i className="ash-tc-dot kind-commit" /><span>{t('monitoring:sa.tc.commit')}</span></span>
                        <span className="ash-tc-track" />
                        <span className="ash-tc-val">{t('monitoring:sa.tc.commitNotSampled')}</span>
                        <span className="ash-tc-sub">
                            {t('monitoring:sa.tc.commitCount', { c: commitStats.commits.toLocaleString(), r: (commitStats.rollbacks ?? 0).toLocaleString() })}
                        </span>
                    </div>
                )}
            </div>

            <div className="ash-tc-legend">
                <span><i style={{ background: CPU_COLOR }} />CPU</span>
                <span><i style={{ background: WAIT_COLOR }} />{t('monitoring:sa.tl.wait')}</span>
                <span><i className="kind-commit" />{t('monitoring:sa.tc.commit')}</span>
            </div>
            <div className="ash-tc-note">
                {t('monitoring:sa.tc.note')}
                {commitStats && commitStats.commits != null && <> {t(comp.pool ? 'monitoring:sa.tc.commitNotePool' : 'monitoring:sa.tc.commitNote', { n: commitStats.sessions })}</>}
                {truncated && <> <b>{t('monitoring:sa.tc.truncated')}</b></>}
            </div>
        </div>
    );
}

TimeComposition.propTypes = {
    comp: PropTypes.object,
    breakdown: PropTypes.array,
    commitStats: PropTypes.shape({ commits: PropTypes.number, rollbacks: PropTypes.number, sessions: PropTypes.number }),
    truncated: PropTypes.bool,
    focusSql: PropTypes.string,
    onFocusSql: PropTypes.func,
    fmtDur: PropTypes.func.isRequired,
};
